import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/components/common/PageHeader";
import { subscriptionService } from "@/services/subscription.service";
import type { SubscriptionListItem } from "@/types";

const SUBSCRIPTION_STATUSES = [
  "ACTIVE",
  "TRIAL",
  "PAST_DUE",
  "CANCELLED",
  "EXPIRED",
] as const;

function SubscriptionRow({
  subscription,
}: {
  subscription: SubscriptionListItem;
}) {
  return (
    <tr className="border-b border-slate-100 last:border-none">
      <td className="px-4 py-3 text-sm font-semibold text-slate-900">
        <Link
          to={`/tenants/${subscription.tenant.id}`}
          className="hover:text-slate-700"
        >
          {subscription.tenant.name}
        </Link>
        <p className="text-xs text-slate-500">{subscription.tenant.email}</p>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">
        <Link
          to={`/subscriptions/${subscription.id}`}
          className="font-semibold text-slate-900 hover:text-slate-700"
        >
          {subscription.plan.name}
        </Link>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">
        {subscription.status}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">
        {subscription.interval}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">
        ₹{subscription.amount}
      </td>
      <td className="px-4 py-3 text-right text-xs text-slate-500">
        {new Date(subscription.startDate).toLocaleDateString()}
      </td>
    </tr>
  );
}

function SubscriptionListPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const queryParams = useMemo(
    () => ({
      page,
      limit,
      search: search.trim() || undefined,
      status: status || undefined,
    }),
    [limit, page, search, status],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["admin-subscriptions", queryParams],
    queryFn: () => subscriptionService.list(queryParams),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Subscriptions"
        subtitle="Track plan assignments, renewals, and billing cadence."
        actions={
          <Link
            to="/subscriptions/plans"
            className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-50"
          >
            Manage plans
          </Link>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Search by tenant name or email"
          className="h-10 flex-1 rounded-xl border border-slate-200 px-4 text-sm focus:border-slate-400 focus:outline-none"
        />
        <select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
          className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm"
        >
          <option value="">All subscription statuses</option>
          {SUBSCRIPTION_STATUSES.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
            <tr>
              <th className="px-4 py-3">Tenant</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Interval</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3 text-right">Start</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={6}>
                  Loading subscriptions...
                </td>
              </tr>
            ) : data?.data.length ? (
              data.data.map((subscription) => (
                <SubscriptionRow
                  key={subscription.id}
                  subscription={subscription}
                />
              ))
            ) : (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={6}>
                  No subscriptions found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>
          Page {data?.meta.page ?? page} of {data?.meta.totalPages ?? 1}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={Boolean(data) && page >= (data?.meta.totalPages ?? 1)}
            onClick={() => setPage((value) => value + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionListPage;
